import { faq } from "@/content/faq";
import { markdownToPlain } from "@/lib/markdownToPlain";
import { Disclosure, Transition } from "@headlessui/react";
import Head from "next/head";
import ReactMarkdown from "react-markdown";
import Image from "next/image";

// variant="blog"  → normal dropdown, divider between items, proper y-spacing  (blog posts & case studies)
// variant="page"  → btn-primary styled button toggle, no divider              (solutions / service pages)
// default (no variant) behaves like "blog"

export default function Faq({ faqName, variant }) {
  const faqList = faq[faqName];
  if (!faqList) return null;

  const isPage = variant === "page";

  const JsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqList.map(({ question, answer }) => ({
      "@type": "Question",
      name: question,
      acceptedAnswer: {
        "@type": "Answer",
        text: markdownToPlain(answer),
      },
    })),
  };

  if (isPage) {
    // ── Page variant: arrow-circle icon, transition, no divider ──────────
    return (
      <>
        <Head>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(JsonLd) }}
          />
        </Head>

        <div className="flex flex-col md:items-left items-center justify-center text-left">
          {faqList.map(({ question, answer }) => (
            <Disclosure key={question + answer}>
              {({ open }) => (
                <>
                  <Disclosure.Button className="w-full min-w-full mb-8">
                    <div className="flex justify-between mb-2 md:mb-0 text-maingray items-center">
                      <h3 className="text-md md:text-xl lg:text-[22px] text-maingray font-semibold text-start">
                        {question}
                      </h3>
                      <Image
                        width={24}
                        height={24}
                        src="/assets/icons/arrow-circle.png"
                        className={open ? "bypass-filter rotate-90 shrink-0 w-6 ml-auto transform text-orange-400" : "shrink-0 w-6 ml-auto text-orange-400"}
                        alt="Dropdown Arrow"
                      />
                    </div>
                  </Disclosure.Button>
                  <Transition
                    enter="transition duration-100 ease-out"
                    enterFrom="transform scale-95 opacity-0"
                    enterTo="transform scale-100 opacity-100"
                    leave="transition duration-75 ease-out"
                    leaveFrom="transform scale-100 opacity-100"
                    leaveTo="transform scale-95 opacity-0"
                  >
                    <Disclosure.Panel>
                      <div className="flex">
                        <div>
                          <div className="mx-auto w-full text-secgray text-justify font-regular mb-8 prose prose-a:text-orange-400 prose-a:underline hover:prose-a:text-orange-500 max-w-none font-['Inter']">
                            <ReactMarkdown>{answer}</ReactMarkdown>
                          </div>
                        </div>
                      </div>
                    </Disclosure.Panel>
                  </Transition>
                </>
              )}
            </Disclosure>
          ))}
        </div>
      </>
    );
  }

  // ── Blog / default variant: normal dropdown, divider, proper y-spacing ──
  return (
    <>
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(JsonLd) }}
        />
      </Head>

      {faqList.map(({ question, answer }, index) => (
        <Disclosure key={question + answer} as="div">
          {({ open }) => (
            <>
              <Disclosure.Button className="flex justify-between w-full px-4 py-5 text-lg font-semibold text-left text-maingray dark:text-white focus:outline-none focus-visible:ring focus-visible:ring-primary-500 focus-visible:ring-opacity-75 group font-['Inter']">
                <span className="group-hover:text-orange-400 transition leading-snug">
                  {question}
                </span>
                <svg
                  className={`${open ? "transform rotate-180" : ""} w-5 h-5 flex-shrink-0 ml-4 mt-1 text-maingray dark:text-white`}
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 12a1 1 0 01-.7-.29l-4-4a1 1 0 111.42-1.42L10 10.59l3.29-3.3a1 1 0 111.42 1.42l-4 4a1 1 0 01-.71.29z"
                    clipRule="evenodd"
                  />
                </svg>
              </Disclosure.Button>

              <Disclosure.Panel className="px-4 pt-1 pb-5 text-base text-secgray leading-relaxed dark:text-gray-300 font-['Inter'] prose prose-a:text-orange-400 prose-a:underline hover:prose-a:text-orange-500 dark:prose-invert max-w-none">
                <ReactMarkdown>{answer}</ReactMarkdown>
              </Disclosure.Panel>

              {index + 1 < faqList.length && (
                <hr className="mt-3 mb-3 border-t border-gray-200 dark:border-gray-700" />
              )}
            </>
          )}
        </Disclosure>
      ))}
    </>
  );
}
